import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, articles, type AnimeArticle } from '@/mocks/anime';

const articleImages: Record<string, string> = {
  '1': 'https://readdy.ai/api/search-image?query=Death%20Note%20anime%20art%20style%2C%20dark%20atmosphere%2C%20a%20mysterious%20black%20notebook%20on%20a%20wooden%20desk%20with%20a%20single%20candle%20casting%20dramatic%20shadows%2C%20gothic%20aesthetic%2C%20deep%20crimson%20and%20black%20tones%2C%20minimalist%20composition%2C%20high%20contrast&width=400&height=560&seq=death-note-card&orientation=portrait',
  '2': 'https://readdy.ai/api/search-image?query=Attack%20on%20Titan%20anime%20epic%20style%2C%20a%20lone%20soldier%20with%20cape%20facing%20a%20massive%20colossal%20figure%20in%20the%20distance%2C%20golden%20sunset%20sky%2C%20dramatic%20scale%2C%20warm%20amber%20lighting%2C%20cinematic%20atmosphere%2C%20powerful%20silhouette&width=400&height=560&seq=aot-card-2026&orientation=portrait',
  '3': 'https://readdy.ai/api/search-image?query=Psycho%20Pass%20anime%20futuristic%20style%2C%20a%20sleek%20holographic%20interface%20displaying%20a%20glowing%20crime%20coefficient%20number%20in%20a%20dark%20surveillance%20room%2C%20neon%20amber%20accents%2C%20cold%20metallic%20surfaces%2C%20cyberpunk%20atmosphere%2C%20clean%20minimalist%20composition&width=400&height=560&seq=psycho-pass-card&orientation=portrait',
  '4': 'https://readdy.ai/api/search-image?query=Monster%20anime%20psychological%20thriller%20style%2C%20a%20hospital%20corridor%20at%20night%20with%20dim%20flickering%20lights%2C%20a%20single%20shadowy%20figure%20standing%20at%20the%20far%20end%2C%20cold%20atmosphere%2C%20deep%20shadows%2C%20ominous%20mood%2C%20high%20tension&width=400&height=560&seq=monster-card&orientation=portrait',
  '5': 'https://readdy.ai/api/search-image?query=Steins%20Gate%20anime%20sci-fi%20style%2C%20a%20vintage%20computer%20monitor%20glowing%20in%20a%20dim%20cluttered%20laboratory%2C%20wires%20and%20gadgets%20scattered%20around%2C%20warm%20amber%20screen%20glow%2C%20mysterious%20atmosphere%2C%20time%20travel%20theme&width=400&height=560&seq=steins-card&orientation=portrait',
  '6': 'https://readdy.ai/api/search-image?query=Nana%20anime%20romance%20style%2C%20a%20vintage%20microphone%20on%20a%20dimly%20lit%20stage%2C%20warm%20spotlight%2C%20rose%20petals%20scattered%2C%20emotional%20atmosphere%2C%20punk%20rock%20aesthetic%2C%20soft%20focus&width=400&height=560&seq=nana-card&orientation=portrait',
  '7': 'https://readdy.ai/api/search-image?query=Code%20Geass%20anime%20mecha%20style%2C%20a%20masked%20figure%20in%20a%20flowing%20cape%20standing%20on%20a%20rooftop%2C%20dramatic%20sunset%20sky%2C%20city%20in%20background%2C%20regal%20purple%20and%20gold%20accents&width=400&height=560&seq=geass-card&orientation=portrait',
  '8': 'https://readdy.ai/api/search-image?query=Paranoia%20Agent%20anime%20surreal%20style%2C%20a%20shadowy%20figure%20with%20a%20bent%20baseball%20bat%20on%20a%20deserted%20street%20at%20night%2C%20single%20streetlamp%20illuminating%2C%20uncanny%20atmosphere%2C%20surreal%20vibe&width=400&height=560&seq=paranoia-card&orientation=portrait',
  '9': 'https://readdy.ai/api/search-image?query=Vinland%20Saga%20anime%20viking%20epic%20style%2C%20a%20lone%20warrior%20standing%20on%20a%20cliff%20overlooking%20a%20stormy%20sea%2C%20dramatic%20clouds%2C%20golden%20light%20breaking%20through%2C%20norse%20aesthetic&width=400&height=560&seq=vinland-card&orientation=portrait',
  '10': 'https://readdy.ai/api/search-image?query=Erased%20anime%20mystery%20style%2C%20a%20snow-covered%20winter%20street%20in%20a%20quiet%20Japanese%20town%2C%20a%20single%20figure%20walking%20away%2C%20nostalgic%20atmosphere%2C%20soft%20blue%20hour%20lighting&width=400&height=560&seq=erased-card&orientation=portrait',
  '11': 'https://readdy.ai/api/search-image?query=Mushishi%20anime%20ethereal%20style%2C%20a%20solitary%20traveler%20with%20white%20hair%20walking%20through%20a%20misty%20bamboo%20forest%2C%20soft%20diffused%20light%20filtering%20through%20leaves%2C%20luminous%20floating%20particles%2C%20serene%20atmosphere%2C%20watercolor%20texture%2C%20gentle%20green%20and%20gold%20tones&width=400&height=560&seq=mushishi-card&orientation=portrait',
  '12': 'https://readdy.ai/api/search-image?query=Showa%20Genroku%20Rakugo%20Shinju%20anime%20traditional%20style%2C%20a%20kimono-clad%20storyteller%20kneeling%20on%20a%20tatami%20stage%20with%20a%20folding%20fan%20under%20warm%20lantern%20light%2C%20traditional%20Japanese%20theater%20setting%2C%20golden%20amber%20lighting%2C%20elegant%20composition&width=400&height=560&seq=rakugo-card&orientation=portrait',
};

export default function CategoryPage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'mystery');
  const [filteredArticles, setFilteredArticles] = useState<AnimeArticle[]>([]);

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  useEffect(() => {
    if (activeCategory === 'all') {
      setFilteredArticles(articles);
    } else {
      setFilteredArticles(articles.filter((a) => a.categorySlug === activeCategory));
    }
  }, [activeCategory]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="breadcrumb">
              <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">
                {t('breadcrumb.home')}
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-700">{t('breadcrumb.category')}</span>
              {currentCategory && (
                <>
                  <i className="ri-arrow-right-s-line"></i>
                  <span className="text-foreground-900 font-medium">{currentCategory.name}</span>
                </>
              )}
            </nav>

            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
              {currentCategory ? currentCategory.name : t('section.categories')}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 mb-8">
              {currentCategory ? currentCategory.description : '浏览全部成人动漫作品分类'}
            </p>

            <div className="flex flex-wrap gap-2 mb-8">
              <Link
                to="/category/mystery"
                onClick={(e) => {
                  e.preventDefault();
                  setActiveCategory('all');
                  window.history.pushState({}, '', '/category/all');
                }}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  activeCategory === 'all'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-50 text-foreground-600 hover:bg-secondary-100 border border-background-200/70'
                }`}
              >
                全部
              </Link>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveCategory(cat.slug);
                    window.history.pushState({}, '', `/category/${cat.slug}`);
                  }}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 hover:bg-secondary-100 border border-background-200/70'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all"
                  >
                    <div className="relative w-full h-[280px] md:h-[320px] overflow-hidden">
                      <img
                        src={articleImages[article.id] || ''}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3 flex items-center gap-1 bg-background-900/80 text-accent-400 px-2.5 py-1 rounded-md text-xs font-bold">
                        <i className="ri-star-fill"></i>
                        {article.rating}
                      </div>
                    </div>
                    <div className="p-4">
                      <span className="inline-block bg-secondary-100 text-secondary-900 text-xs px-2 py-0.5 rounded-md mb-2">
                        {article.category}
                      </span>
                      <h3 className="text-base font-heading font-bold text-foreground-900 group-hover:text-primary-500 transition-colors mb-2">
                        {article.title}
                      </h3>
                      <p className="text-xs text-foreground-600 line-clamp-2 leading-relaxed">
                        {article.synopsis.slice(0, 80)}...
                      </p>
                      <div className="flex items-center gap-4 mt-3 text-xs text-foreground-500">
                        <span>{article.episodes}</span>
                        <span>{article.year}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-emotion-sad-line text-4xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">{t('noResults')}</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}